import React, { ReactNode, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { keyframes } from 'glamor';
import Skeleton from 'react-loading-skeleton';

import { BarCode, PhysicalCard, Reload } from '../../../../components/custom-icons';

import { OrderSummaryGroupItems } from './OrderSummaryItems';

import { createStyles } from 'utils/createStyle';
import { CartResponse } from 'common/api/e-comm/cart/lineitem/models/CartItems';
import { ProductType } from 'common/api/e-comm/models/Product';
import { useRootSelector } from 'rootStore';
import { monetizeWithDecimal } from 'common/utils/strings';
import { orgSupportsShipToHome } from 'common/utils/utils';
import { OrderScreenContext } from 'features/order-summary/screens/CheckoutScreen';
import { CartLineItem } from 'common/api/e-comm/models/CartLineItem';
import { EASE_IN_OUT_BACK } from 'styles/easing';
import { calculatePaymentFee } from 'features/order-summary/utils/useCartDetails';
import { getPaymentDisplayName } from 'features/order-summary/utils';
import { useWindowInnerSize } from 'hooks/useWindowSize';
import { OrderSummaryOptions } from './OrderSummaryOptions';

const bigBtnSelectedAnimation = (low = 60, high = 77) =>
    keyframes({
        '0%': {
            height: `${low}px`,
            opacity: 1,
        },
        '20%': { opacity: 0.6 },
        '100%': {
            height: `${high}px`,
            fontSize: '12px',
            opacity: 1,
        },
    });

const bigBtnUnselectedAnimation = (low = 60, high = 77) =>
    keyframes({
        '0%': {
            height: `${high}px`,
            opacity: 1,
        },
        '20%': { opacity: 0.6 },
        '100%': {
            height: `${low}px`,
            fontSize: '10px',
            opacity: 1,
        },
    });

const showOpacity = keyframes({
    '0%': {
        opacity: 0.6,
    },
    '100%': {
        opacity: '1',
    },
});

const styles = createStyles({
    container: '',
    header: ['pt-2'],
    headerShipToHome: [
        'duration-300',
        {
            background:
                'linear-gradient(180deg, rgba(241, 90, 41, 0.1) 0%, rgba(230, 18, 106, 0.1) 42.35%, rgba(126, 63, 151, 0.1) 88.48%)',
            borderRadius: '10px 10px 0 0',
            marginBottom: '10px',
        },
    ],
    shippingOptionButtons: [
        'block lg:grid gap-5 py-4 px-4 items-center',
        {
            gridTemplateColumns: '1fr 1fr',
            minHeight: '110px',
        },
    ],
    summaryGroupItems: [
        'grid w-full',
        {
            gridTemplateColumns: '2fr 1fr 1fr 1fr 2rem',
            rowGap: '1rem',
        },
    ],
    sendToHomeSummaryGroupItems: ['px-8 pb-4 lg:px-0 lg:pb-0 lg:pl-4 col-span-3'],
    productTypeSeparator: [
        'py-4 px-5',
        {
            animationName: showOpacity,
            animationDuration: '.5s',
            animationTimingFunction: EASE_IN_OUT_BACK,
            animationFillMode: 'forwards',
        },
    ],
    showDeliveryGroup: [
        '',
        {
            animationName: showOpacity,
            animationDuration: '.5s',
            animationTimingFunction: EASE_IN_OUT_BACK,
            animationFillMode: 'forwards',
        },
    ],
    columns: [
        'lg:border-b border-solid border-grey-3',
        {
            backgroundColor: '#F7F7F7',
        },
    ],
    footer: [
        '',
        {
            backgroundColor: '#F7F7F7',
            borderRadius: '0 0 10px 10px',
        },
    ],
    bigBtnDescriptionSelected: ['text-grey-1', { fontSize: '12px' }],
    bigBtnDescriptionUnselected: ['text-grey-1', { fontSize: '10px' }],
    total: [
        'grid w-full',
        {
            gridTemplateColumns: '2fr 1fr 1fr 1fr 2rem',
            rowGap: '0.4rem',
        },
    ],
    footerSummaryTotal: 'text-left text-xs text-grey-1',
});

export const getProductTypeIcon = (productType: ProductType, size_?: string) => {
    const size = size_ || '1.25rem'; // default to 1.25rem
    if (productType === ProductType.EGiftCard) {
        return <BarCode width={size} height={size} />;
    }

    if (productType === ProductType.GiftCard) {
        return <PhysicalCard width={size} height={size} />;
    }

    if (productType === ProductType.Reload) {
        return <Reload width={size} height={size} />;
    }

    return null;
};

export const getProductTypeDisplayName = (productType: ProductType) => {
    if (productType === ProductType.EGiftCard) {
        return 'eGift';
    }

    if (productType === ProductType.GiftCard) {
        return 'Physical';
    }

    if (productType === ProductType.Reload) {
        return 'Reload';
    }
};

export interface BigBtnProps {
    selected: boolean;
    title: ReactNode;
    description: ReactNode;
    onClick: () => void;
    sizes?: { max: number; from: number; to: number }[];
}

const defaultSize = { max: 0, from: 65, to: 80 };
const BigBtn = ({ selected, title, description, sizes, ...props }: BigBtnProps) => {
    const { width } = useWindowInnerSize();

    const btnSize = useMemo(() => {
        const result = sizes && sizes.sort((a, b) => a.max - b.max).find((it) => it.max >= width);
        return result || defaultSize;
    }, [sizes, width]);

    const dynamicStyles = useMemo(
        () =>
            createStyles({
                bigBtn: [
                    'flex flex-row justify-center items-center border border-brand-accent bg-white px-4 sm:px-8 w-full',
                    {
                        ':focus': {
                            outline: 'none',
                        },
                        overflow: 'hidden',
                        borderRadius: '10px',
                        animationName: bigBtnSelectedAnimation(btnSize.from, btnSize.to),
                        animationDuration: '.5s',
                        animationTimingFunction: EASE_IN_OUT_BACK,
                        animationFillMode: 'forwards',
                    },
                ],
                bigBtnUnselected: [
                    'border border-grey-2 focus:border-brand-accent bg-white/75 px-4 sm:px-8 text-grey-2 w-full',
                    {
                        ':focus': {
                            outline: 'none',
                        },
                        overflow: 'hidden',
                        borderRadius: '10px',
                        animationName: bigBtnUnselectedAnimation(btnSize.from, btnSize.to),
                        animationDuration: '.5s',
                        animationTimingFunction: EASE_IN_OUT_BACK,
                        animationFillMode: 'forwards',
                    },
                ],
            }),
        [btnSize]
    );

    return (
        <button
            type="button"
            className={selected ? dynamicStyles.bigBtn : dynamicStyles.bigBtnUnselected}
            {...props}
        >
            <div>
                <div
                    className={`flex justify-center items-center text-lg ${
                        selected ? 'text-brand' : 'text-grey-2'
                    } leading-1`}
                >
                    <span className="inline-block">{title}</span>
                </div>
                <div
                    className={`${
                        selected
                            ? styles.bigBtnDescriptionSelected
                            : styles.bigBtnDescriptionUnselected
                    }`}
                >
                    {description}
                </div>
            </div>
        </button>
    );
};

export interface OrderSummaryGridProps {
    cart: CartResponse;
    loading: boolean;
    shipToHome: boolean;
    onShipToHomeChange: (shipToHome: boolean) => void;
    hideButtons?: boolean;
    summaryType?: 'subtotal' | 'total';
    hideEditQuantityButtons?: boolean;
    hideDeleteButton?: boolean;
}

export const OrderSummaryGrid = ({
    cart,
    loading,
    shipToHome,
    onShipToHomeChange,
    hideButtons,
    summaryType,
    hideEditQuantityButtons,
    hideDeleteButton = false,
}: OrderSummaryGridProps) => {
    const { cartInfoDetails } = useContext(OrderScreenContext);
    const {
        hasShipToHomeEligibleItems,
        shipToHomeItems,
        shipToCoordinator,
        electronicDeliveryItems,
        shipping: { shippingFee, isLoading: isLoadingShipping },
        payment: { paymentFee },
        total,
    } = cartInfoDetails;

    const organization = useRootSelector((s) => s.store.organization);
    const organizationSupportsShipToHome = orgSupportsShipToHome(organization);

    const renderDeliveryGroup = (title: string, description: string, items: CartLineItem[]) => {
        return (
            <>
                <div className={items.length <= 0 ? 'hidden' : styles.showDeliveryGroup}>
                    <div className={`${styles.productTypeSeparator}}`}>
                        {
                            <div className="pb-4">
                                <div className="block text-xl text-brand">{title}</div>
                                <div className="block text-grey-1 text-xs">{description}</div>
                            </div>
                        }
                        <div>
                            {
                                <OrderSummaryGroupItems
                                    items={items}
                                    loading={loading}
                                    hideButtons={hideEditQuantityButtons}
                                    hideDeleteButton={hideDeleteButton}
                                />
                            }
                        </div>
                    </div>
                    <div className="border-b border-solid border-grey-3" />
                </div>
            </>
        );
    };

    const renderPayment = () => {
        return (
            paymentFee &&
            paymentFee.amount > 0 && (
                <>
                    <div className={classNames(styles.footerSummaryTotal, 'col-span-2')}>
                        {getPaymentDisplayName(paymentFee?.paymentType)}
                    </div>
                    <div />
                    <div className="col-span-2 sm:col-span-1 text-right sm:text-center text-brand-dark">
                        {monetizeWithDecimal(
                            calculatePaymentFee((cart?.subtotal || 0) + shippingFee, paymentFee)
                        )}
                    </div>
                    <div className="hidden sm:block text-center" />
                </>
            )
        );
    };

    return (
        <div className={styles.container}>
            {!hideButtons && organizationSupportsShipToHome && (
                <div
                    className={classNames(
                        styles.header,
                        hasShipToHomeEligibleItems ? styles.headerShipToHome : ''
                    )}
                >
                    {hasShipToHomeEligibleItems && (
                        <>
                            <div className="text-center">
                                <div className="text-brand font-bold">
                                    You have cards eligible for ship to home.{' '}
                                </div>
                            </div>
                            <div className={styles.shippingOptionButtons}>
                                <BigBtn
                                    selected={shipToHome}
                                    title={
                                        <div>
                                            <span className="inline-block">Ship them to me</span>
                                        </div>
                                    }
                                    description={
                                        <div>
                                            I’d like them sent directly to me.{' '}
                                            <span className="truncate">(S&amp;H fees apply)</span>
                                        </div>
                                    }
                                    onClick={() => onShipToHomeChange(true)}
                                    sizes={[
                                        { max: 350, from: 90, to: 100 },
                                        { max: 450, from: 80, to: 100 },
                                    ]}
                                />
                                <div className="block lg:hidden mt-2" />
                                <BigBtn
                                    selected={!shipToHome}
                                    title={
                                        <div>
                                            <span className="inline-block">
                                                Ship them to my coordinator
                                            </span>
                                        </div>
                                    }
                                    description={
                                        <>
                                            <span>I’d like them sent to my coordinator.</span>{' '}
                                            <span className="font-bold truncate">
                                                (Organization schedules and pays S&H fees)
                                            </span>
                                        </>
                                    }
                                    onClick={() => onShipToHomeChange(false)}
                                    sizes={[
                                        { max: 350, from: 100, to: 120 },
                                        { max: 450, from: 80, to: 100 },
                                    ]}
                                />
                            </div>
                        </>
                    )}
                </div>
            )}
            <div />
            <div className={styles.columns}>
                <div
                    className={classNames(
                        styles.summaryGroupItems,
                        'text-left px-5 text-sm py-4 text-brand-dark font-bold hidden lg:grid'
                    )}
                >
                    <div>Product</div>
                    <div className="text-center">Value</div>
                    <div className="text-center">Quantity</div>
                    <div className="text-center">Dollar Amount</div>
                </div>
            </div>
            {renderDeliveryGroup(
                'Ship to Home',
                'These cards will ship directly to you.',
                shipToHomeItems
            )}
            {renderDeliveryGroup(
                'Ship to Coordinator',
                'These cards will be sent to your Coordinator.',
                shipToCoordinator
            )}
            {renderDeliveryGroup('Electronic Delivery', '', electronicDeliveryItems)}
            {(!summaryType || summaryType === 'subtotal') && (
                <div className={classNames(styles.footer)}>
                    <div
                        className={classNames(
                            styles.summaryGroupItems,
                            'text-center px-5 text-sm py-4 text-brand-dark font-bold grid'
                        )}
                    >
                        <div className="text-xs md:text-sm flex items-center justify-center">
                            Subtotal
                        </div>
                        <div className="hidden lg:block" />
                        <div className="text-sm flex items-center justify-center col-span-2 lg:col-span-1">
                            <span className="hidden sm:block">{cart.productCount}</span>
                        </div>
                        <div className="text-base flex items-center justify-center">
                            {monetizeWithDecimal(cart.subtotal)}
                        </div>
                    </div>
                </div>
            )}
            {summaryType === 'total' && (
                <>
                    <div
                        className={classNames(
                            styles.total,
                            'hidden md:grid text-center px-5 text-sm py-4 grid'
                        )}
                    >
                        <div className={styles.footerSummaryTotal}>Subtotal</div>
                        <div />
                        <div className="hidden sm:block text-grey-1">{cart.productCount}</div>
                        <div className="block sm:hidden" />
                        <div className="col-span-2 sm:col-span-1 text-right sm:text-center text-brand-dark font-bold">
                            {monetizeWithDecimal(cart.subtotal)}
                        </div>
                        <div className="hidden sm:block text-center" />

                        {renderPayment()}

                        <div className={classNames(styles.footerSummaryTotal, 'col-span-2')}>
                            Shipping &amp; Handling
                        </div>
                        <div />
                        <div className="col-span-2 sm:col-span-1 text-right sm:text-center text-brand-dark">
                            {isLoadingShipping ? <Skeleton /> : monetizeWithDecimal(shippingFee)}
                        </div>
                        <div className="hidden sm:block text-center" />
                    </div>
                    <div className={classNames(styles.footer)}>
                        <div
                            className={classNames(
                                styles.total,
                                'hidden md:grid border-t border-solid border-grey-3 text-center px-5 text-sm py-4 text-brand-dark font-bold grid'
                            )}
                        >
                            <div className="col-span-2" />
                            <div className="text-xs text-center flex items-center justify-center">
                                Total
                            </div>
                            <div className="col-span-2 sm:col-span-1 text-right sm:text-center text-lg">
                                {isLoadingShipping ? <Skeleton /> : monetizeWithDecimal(total)}
                            </div>
                            <div className="hidden sm:block text-center" />
                        </div>
                    </div>
                    <div className="p-4 md:hidden">
                        <OrderSummaryOptions shipToHome={shipToHome} hidePayment={false} />
                    </div>
                </>
            )}
        </div>
    );
};
